import { Fragment, h } from 'preact';
import { useEffect, useState, useReducer, useMemo } from 'preact/hooks';
import Panel from 'utils/panel';
import { IconClose } from 'utils/icon';
import Image from 'utils/image';
import { formatMoney } from 'utils/storefront/format-resource';
import ProductMonogrammingCanvas from './product-monogramming-canvas';
import ProductMonogrammingPreview from './product-monogramming-preview';
import Monograms from './monograms';
import Names from './names';
import Motifs from './motifs';

function reducer(state, action) {
  // eslint-disable-next-line default-case
  switch (action.type) {
    case 'init_state': {
      return action.payload;
    }
    case 'change_monogram': {
      return {
        ...state,
        monograms: action.payload,
      };
    }
    case 'change_motif': {
      return {
        ...state,
        motifs: action.payload,
      };
    }
    case 'change_name': {
      return {
        ...state,
        names: action.payload,
      };
    }
    case 'change_tab': {
      return {
        ...state,
        activeTab: action.payload,
      };
    }
    case 'change_applied_tab': {
      return {
        ...state,
        appliedTab: action.payload,
      };
    }
    case 'reset_active_tab_text': {
      return {
        ...state,
        [state.activeTab]: {
          ...state[state.activeTab],
          text: '',
        },
      };
    }
    case 'remove_active': {
      return { ...state, appliedTab: '' };
    }
  }
  throw Error(`Unknown action: ${action.type}`);
}

const emptyState = {
  activeTab: 'monograms',
  appliedTab: '',
  monograms: {
    font: '',
    style: '',
    color: '',
    colorHex: '',
    text: '',
    textMaxLength: 3,
  },
  names: {
    style: '',
    font: '',
    color: '',
    colorHex: '',
    text: '',
    textMaxLength: 12,
  },
  motifs: {
    style: '',
    font: '',
    color: '',
    colorHex: '',
    text: '',
    optionalFont: '',
    textMaxLength: 12,
    motifImage: '',
  },
};

const ProductMonogramming = ({
  product,
  updateMonogram,
  customizableOptionTag,
}) => {
  const isMonogramProduct = product?.tags.includes(customizableOptionTag);
  const [monogrammingData, setMonogrammingData] = useState([]);
  const [state, dispatch] = useReducer(reducer, emptyState);
  const [tabs, setTabs] = useState([]);
  const customizerSwatchesData = monogrammingData?.find(
    (data) => data.name === 'product-customizer-swatches',
  );
  const customizerMotifsData = monogrammingData?.find(
    (data) => data.name === 'product-customizer-motifs',
  );

  if (!product.metafields?.customize) return null;

  let { monogram, names, motif } = product.metafields.customize;
  if (typeof monogram === 'string') monogram = JSON.parse(monogram);
  if (typeof names === 'string') names = JSON.parse(names);
  if (typeof motif === 'string') motif = JSON.parse(motif);

  const normalizedMetafields = {
    ...product.metafields.customize,
    monogram: monogram || [],
    names: names || [],
    motif: motif || [],
  };
  useEffect(() => {
    const sections = document.querySelectorAll(
      '[data-customizer-type=product-customizer-motifs],[data-customizer-type=product-customizer-swatches]',
    );
    const additionalBlocks = document.querySelectorAll(
      '[data-customizer-type=product-customizer-extra-monogram-blocks]',
    );

    const sectionsData = Array.from(sections).map((section) => {
      const dataEl = section.querySelector('[data-section-data]');
      const data = dataEl ? JSON.parse(dataEl.textContent) : {};
      return { name: section.dataset.customizerType, data };
    });

    const swatchesIndex = sectionsData.findIndex(
      (data) => data.name === 'product-customizer-swatches',
    );

    additionalBlocks.forEach((block) => {
      const dataElextra = block.querySelector('[data-section-data]');
      const data = dataElextra.textContent
        ? JSON.parse(dataElextra.textContent)
        : null;

      if (!data) return;
      if (swatchesIndex !== -1) {
        sectionsData[swatchesIndex].data.blocks.push(...data.blocks);
      }
    });

    setMonogrammingData(sectionsData);
  }, []);

  useEffect(async () => {
    const swatches = monogrammingData.find(
      (data) => data.name === 'product-customizer-swatches',
    );
    const motifs = monogrammingData.find(
      (data) => data.name === 'product-customizer-motifs',
    );

    let initialState = {
      ...state,
      metafieldData: {
        ...normalizedMetafields,
      },
    };

    const tempTabs = [];

    if (monogram && monogram.length > 0) {
      tempTabs.push('monograms');
      let defaultPlacement = {
        label: normalizedMetafields?.placement || 'Standard',
        horizontal: monogram[0].style.horizontal,
        vertical: monogram[0].style.vertical,
      };

      const { placements } = monogram[0];
      if (placements && placements.length > 0) {
        [defaultPlacement] = placements;
      }

      initialState = {
        ...initialState,
        monograms: {
          font: '',
          placement: defaultPlacement || '',
          style: monogram[0]?.style?.label || '',
          color: monogram[0]?.colors[0] || '',
          colorHex:
            (swatches?.data?.blocks || []).find(
              (block) =>
                block.title === monogram[0]?.colors[0] &&
                block.type === 'swatch-color',
            )?.color || '',
          text: monogram[0]?.text?.initialValue || '',
          textMaxLength: monogram[0]?.text?.maxLength || 3,
        },
      };
    }

    if (names && names.length > 0) {
      tempTabs.push('names');

      let defaultPlacement = {
        label: normalizedMetafields?.placement || 'Standard',
        horizontal: names[0].style.horizontal,
        vertical: names[0].style.vertical,
      };

      const { placements } = names[0];
      if (placements && placements.length > 0) {
        [defaultPlacement] = placements;
      }
      initialState = {
        ...initialState,
        names: {
          font: names[0]?.style?.label || '',
          placement: defaultPlacement || '',
          style: '',
          color: names[0]?.colors[0] || '',
          colorHex:
            (swatches?.data?.blocks || []).find(
              (block) =>
                block.title === names[0]?.colors[0] &&
                block.type === 'swatch-color',
            )?.color || '',
          text: names[0]?.text?.initialValue || '',
          textMaxLength: names[0]?.text?.maxLength || 12,
        },
      };
    }

    if (motif && motif.length > 0) {
      tempTabs.push('motifs');

      let defaultPlacement = {
        label: normalizedMetafields?.placement || 'Standard',
        horizontal: motif[0].style.horizontal,
        vertical: motif[0].style.vertical,
        scale: motif[0].style?.scale,
      };

      const { placements } = motif[0];
      if (placements && placements.length > 0) {
        [defaultPlacement] = placements;
      }

      initialState = {
        ...initialState,
        motifs: {
          font: '',
          placement: defaultPlacement || '',
          style: motif[0]?.style?.label || '',
          color: motif[0]?.colors ? motif[0].colors[0] : '', // Optional field
          colorHex: motif[0]?.colors
            ? (swatches?.data?.blocks || []).find(
                (block) =>
                  block.title === motif[0]?.colors[0] &&
                  block.type === 'swatch-color',
              )?.color
            : '', // Optional field
          optionalFont: motif[0]?.optionalFonts
            ? motif[0]?.optionalFonts[0]
            : '', // Optional field
          text: '', // Optional field, blank on initial load
          textMaxLength: motif[0]?.text ? motif[0].text.maxLength : 12, // Optional field,
          textMinLength: motif[0]?.text ? motif[0].text.minLength : 1, // Optional field
          motifImage:
            (motifs?.data?.blocks || []).find(
              (block) =>
                block.title === motif[0]?.style?.label &&
                block.type === 'swatch-image',
            )?.image || '',
        },
      };
    }

    setTabs(tempTabs);

    dispatch({
      type: 'init_state',
      payload: initialState,
    });

    document.addEventListener('monogram:reset', () => {
      dispatch({ type: 'init_state', payload: initialState });
    });
  }, [monogrammingData, setTabs]);

  const renderPrice = useMemo(() => {
    const swatchesData = monogrammingData.find(
      (data) => data.name === 'product-customizer-swatches',
    );
    const monogramProductActiveVariant =
      state.activeTab === 'motifs' &&
      state[state.activeTab]?.text &&
      state[state.activeTab]?.text !== ''
        ? swatchesData?.data?.monogram_product?.variants[1]
        : swatchesData?.data?.monogram_product?.variants[0];

    return monogramProductActiveVariant?.price
      ? formatMoney({ amount: monogramProductActiveVariant.price }, true, true)
      : '';
  }, [monogrammingData, state]);

  const renderMinPrice = useMemo(() => {
    const swatchesData = monogrammingData.find(
      (data) => data.name === 'product-customizer-swatches',
    );

    return swatchesData?.data?.monogram_product?.price_min
      ? formatMoney(
          {
            amount: swatchesData?.data?.monogram_product?.price_min,
          },
          true,
          true,
        )
      : '';
  });

  const renderFee = useMemo(() => {
    const swatchesData = monogrammingData.find(
      (data) => data.name === 'product-customizer-swatches',
    );
    const monogramProductActiveVariant =
      state.appliedTab === 'motifs' &&
      state[state.appliedTab]?.text &&
      state[state.appliedTab]?.text !== ''
        ? swatchesData?.data?.monogram_product?.variants[1]
        : swatchesData?.data?.monogram_product?.variants[0];

    return monogramProductActiveVariant?.price
      ? formatMoney({ amount: monogramProductActiveVariant.price }, true, true)
      : '';
  }, [monogrammingData, state]);

  const applyDesign = () => {
    const attributes = [{ key: '_charge_type', value: 'monogram' }];
    const currentTabName = state.activeTab;
    const currentTab = state[state.activeTab];

    if (currentTabName === 'motifs') {
      attributes.push({
        key: 'MONOGRAM_FONT',
        value: currentTab.style,
      });

      if (currentTab.optionalFont !== '') {
        attributes.push(
          {
            key: 'MONOGRAM_OPTIONAL_FONT',
            value: currentTab.optionalFont,
          },
          {
            key: 'MONOGRAM_TEXT',
            value: currentTab.text,
          },
          {
            key: 'MONOGRAM_COLOR_NAME',
            value: currentTab.color,
          },
        );
      }
    } else if (currentTabName === 'names') {
      attributes.push(
        {
          key: 'MONOGRAM_FONT',
          value: currentTab.font,
        },
        {
          key: 'MONOGRAM_TEXT',
          value: currentTab.text,
        },
        {
          key: 'MONOGRAM_COLOR_NAME',
          value: currentTab.color,
        },
      );
    }
    // Monograms
    else {
      attributes.push(
        {
          key: 'MONOGRAM_FONT',
          value: currentTab.style,
        },
        {
          key: 'MONOGRAM_TEXT',
          value: currentTab.text,
        },
        {
          key: 'MONOGRAM_COLOR_NAME',
          value: currentTab.color,
        },
      );
    }

    attributes.push({
      key: 'MONOGRAM_PLACEMENT_NAME',
      value: currentTab.placement.label,
    });

    attributes.push({
      key: '_monogram_type',
      value: currentTabName,
    });

    dispatch({
      type: 'change_applied_tab',
      payload: state.activeTab,
    });

    document.dispatchEvent(new Event('product-monogram:close'));
    const swatchesData = monogrammingData.find(
      (data) => data.name === 'product-customizer-swatches',
    );
    const monogramProductActiveVariant =
      state.appliedTab === 'motifs' &&
      state[state.appliedTab]?.text &&
      state[state.appliedTab]?.text !== ''
        ? swatchesData?.data?.monogram_product?.variants[1]
        : swatchesData?.data?.monogram_product?.variants[0];

    updateMonogram({
      // eslint-disable-next-line max-len
      merchandiseId: `gid://shopify/ProductVariant/${monogramProductActiveVariant?.id}`,
      attributes,
    });

    document
      .querySelector('[data-monogram-overlay]')
      .classList.remove('monogram-overlay');
  };

  const changeTab = (tab) => {
    dispatch({
      type: 'change_tab',
      payload: tab,
    });
  };

  const onClickPersonalize = () => {
    changeTab(state.appliedTab || tabs[0]);
    document.dispatchEvent(new Event('product-monogram:open'));
    document
      .querySelector('[data-monogram-overlay]')
      .classList.add('monogram-overlay');
  };

  const onPanelClose = () => {
    document
      .querySelector('[data-monogram-overlay]')
      .classList.remove('monogram-overlay');
    document.dispatchEvent(new Event('product-monogram:close'));
  };

  const onClickReset = () => {
    dispatch({ type: 'reset_active_tab_text' });
  };

  const removePersonalize = () => {
    dispatch({ type: 'remove_active' });
    updateMonogram(false);
  };

  const applyDesignDisabled = () => {
    const tabData = state[state.activeTab];

    if (state.activeTab === 'monograms' || state.activeTab === 'names') {
      const isEmpty = !tabData?.text || tabData.text.trim() === '';
      const isTooLong = tabData?.text.length > tabData?.textMaxLength;
      return isEmpty || isTooLong;
    }
    // Motif is always selected, and font is optional
    return false;
  };

  if (tabs.length === 0) return <></>;

  if (!isMonogramProduct) return null;

  return (
    <Fragment>
      <div className="pt-4">
        <div className="flex mb-4 align-center justify-between">
          <span className="flex align-center" onClick={onClickPersonalize}>
            <input
              type="checkbox"
              className="hidden"
              id="personalize"
              checked={state.appliedTab !== ''}
            />
            <label
              htmlFor="personalize"
              className="h7 text-uppercase flex items-center cursor-pointer"
            >
              <span className="w-5 h-5 inline-block mr-2.5 border border-gray-900"></span>
              {window.TMW.Strings.product.personalizeIt}
            </label>
          </span>
          {state.appliedTab === '' ? (
            <span className="body-3">{renderMinPrice}+</span>
          ) : (
            <button
              type="button"
              className="text-sm body-3 underline"
              onClick={onClickPersonalize}
            >
              {window.TMW.Strings.product.edit}
            </button>
          )}
        </div>
        {state.appliedTab !== '' && (
          <div>
            <div
              className={`w-full bg-gray-300 mb-2.5 relative ${
                state.appliedTab === 'motifs' ? 'h-[180px]' : 'h-[100px]'
              }`}
            >
              <ProductMonogrammingPreview
                customize={state.metafieldData}
                state={state}
              />
            </div>
            <div className="mb-2.5 px-3 flex items-start justify-between">
              <div>
                <div className="h7">Personalization fee</div>
                <div>{renderFee}</div>
              </div>
              <div className="body-3 underline flex gap-x-1 items-center">
                <button
                  type="button"
                  className="text-sm body-3 underline"
                  onClick={onClickPersonalize}
                >
                  {window.TMW.Strings.product.edit}
                </button>
                <span>|</span>
                <button
                  type="button"
                  className="text-sm body-3 underline"
                  onClick={removePersonalize}
                >
                  {window.TMW.Strings.product.remove}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Panel
        overlay={false}
        alignment="right"
        bodyScrollLock={true}
        classes={'w-[375px] md:w-[920px]'}
        context="product-monogram"
      >
        <div className="h-full flex flex-col" data-monogram-overlay>
          <div className="flex items-center justify-between px-6 min-h-[72px] bg-background-2">
            <h3 className="h3">{window.TMW.Strings.product.personalize}</h3>
            <button type="button" onClick={onPanelClose}>
              <IconClose />
            </button>
          </div>
          <div className="overflow-y-auto md:flex block h-[calc(100%-72px)]">
            <div className="bg-background-2 flex-1 h-[375px] md:h-auto">
              <div className="flex flex-col">
                <div className="relative overflow-hidden">
                  <Image
                    src={
                      product.metafields.customize_image ||
                      normalizedMetafields.image
                    }
                    classes="w-full h-full object-contain"
                  />
                  <ProductMonogrammingCanvas
                    customize={state.metafieldData}
                    state={state}
                  />
                </div>
                {monogrammingData.find(
                  (data) => data.name === 'product-customizer-swatches',
                )?.data?.decorative_image && (
                  <div className="w-full py-7 hidden md:block">
                    <Image
                      src={
                        monogrammingData.find(
                          (data) => data.name === 'product-customizer-swatches',
                        )?.data?.decorative_image
                      }
                      classes="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col flex-1 h-full">
              <div className="py-5 md:py-[30px] h-dvh md:h-[calc(100%-109px)]">
                <div className="text-center h7--medium px-5 md:px-10">
                  <ul className="flex flex-wrap">
                    {tabs.map((tab) => (
                      <li className="flex-1" key={tab}>
                        <button
                          type="button"
                          onClick={() => changeTab(tab)}
                          // eslint-disable-next-line max-len
                          className={`uppercase inline-block border-b-[3px] w-full pb-3 hover:border-gray-900 hover:text-text-default transition-all tracking-[0.84px] md:tracking-[1px] ${
                            state.activeTab === tab
                              ? 'border-gray-900 text-text-default'
                              : 'border-gray-500 text-text-disabled'
                          }`}
                        >
                          {tab}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="overflow-y-auto px-5 md:px-10 mt-5 py-5 md:h-[calc(100%-24px)] h-[calc(100%-34px)]">
                  {state.activeTab === 'monograms' && (
                    <Monograms
                      customizerSwatchesData={customizerSwatchesData}
                      configuration={monogram}
                      state={state}
                      activeTab={state[state.activeTab]}
                      dispatch={dispatch}
                    />
                  )}
                  {state.activeTab === 'names' && (
                    <Names
                      customizerSwatchesData={customizerSwatchesData}
                      configuration={names}
                      state={state}
                      activeTab={state[state.activeTab]}
                      dispatch={dispatch}
                    />
                  )}
                  {state.activeTab === 'motifs' && (
                    <Motifs
                      customizerSwatchesData={customizerSwatchesData}
                      customizerMotifsData={customizerMotifsData}
                      configuration={motif}
                      state={state}
                      activeTab={state[state.activeTab]}
                      dispatch={dispatch}
                    />
                  )}

                  <div className="pb-5">
                    <div className="body-3 mb-2.5">
                      <p className="font-medium pb-2.5 flex items-center">
                        {window.TMW.Strings.product.personalizationFee}:
                        {renderPrice} {window.TMW.Strings.product.perItem}
                      </p>
                      <p className="">
                        {window.TMW.Strings.product.personalizePolicy}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 shadow shadow-3xl h-[109px]">
                <div className="h7--medium flex items-center justify-between pb-4">
                  <div className="text-uppercase">
                    {window.TMW.Strings.product.subtotal}
                  </div>
                  <div className="">{renderPrice}</div>
                </div>
                <div className="flex items-center justify-between gap-x-2.5">
                  <button
                    className="button button--primary button--primary--outline-black w-full px-1 border-gray-500 hover:border-gray-900"
                    type="button"
                    onClick={onClickReset}
                  >
                    {window.TMW.Strings.product.reset}
                  </button>
                  <button
                    className={`button button--primary w-full px-1 ${
                      state[state.activeTab]?.text === '' ? 'disabled' : ''
                    }`}
                    disabled={applyDesignDisabled()}
                    type="button"
                    onClick={applyDesign}
                  >
                    {window.TMW.Strings.product.applyDesign}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </Fragment>
  );
};

export default ProductMonogramming;
